import { carInfoModalAtom } from '@/commons/constants';
import { PlanCar } from '@/types/plan';
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import CustomTimePicker from '@/commons/CustomTimePicker';
import * as _ from 'lodash';
import { CustomRadio } from '@/components/exec/common/CustomMui';
import {
  cargoListAtom,
  centerListAtom,
  planCarListAtom,
  postCoordInfo,
  putCars,
  putCoordInfo,
} from '@/interfaces/plan/manage';
import CoordMap from '@/components/exec/common/coordMap';
import { regionAtom } from '@/interfaces/region';
import { RegionGroup } from '@/types/regions';

const ModalWrap = styled.div`
  position: absolute;
  top:50%;
  left:50%;
  transform: translate(-50%, -50%);
  width: 25%;
  height: 38rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .head{
    padding: 0.5rem;
    width: 100%;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background:rgba(82, 133, 164, 0.87);
    color: #ffffff;
  }
`;

const MainContentWrap = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 1rem;
  
  .row{
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
  }
`;

interface CarInfoProps {
  target: PlanCar[];
}

// export const carInfoModifyListAtom = atom<PlanCar[]>();

export default function CarInfo({ target }: CarInfoProps) {
  const [carInfoModal, setCarInfoModal] = useAtom(carInfoModalAtom);
  const { refetch } = useAtomValue(planCarListAtom);
  const { data: regionList } = useAtomValue(regionAtom);
  const [modifyInfo, setModifyInfo] = useState<PlanCar>();

  const [coordMap, setCoordMap] = useState<boolean>(false);

  const [cargoInfo, setCargoInfo] = useState<any>();

  const handleClose = () => {
    setCarInfoModal(false);
  };

  useEffect(() => {
    if (target && target.length > 0) {
      setModifyInfo(target[0]);
      if (target.length === 1) {
        setCargoInfo({
          seq: target[0].cargo_seq,
          lat: target[0].cargo_lat,
          lng: target[0].cargo_lng,
          sido: target[0].cargo_sido,
          sigugun: target[0].cargo_dongmyun,
          ri: target[0].cargo_ri,
          postal_code: target[0].cargo_postal_code,
          address: target[0].cargo_address,
        });
      }
    }
  }, [target]);

  const handleSaveClick = async () => {
    const param = {
      ...modifyInfo,
      targetSeqList: _.map(target, (v) => v.seq),
      lunch_time: JSON.stringify((modifyInfo.lunch_time || []).map((t: string | null) => t ?? '00:00')),
    };
    await putCars(param);
    if (target.length === 1) {
      if (target[0].cargo_seq > 0) {
        await putCoordInfo(cargoInfo);
      } else {
        await postCoordInfo({ ref_seq: target[0].seq, type: 'CARGO', name: target[0].name + ' 차고', ...cargoInfo });
      }
    }
    refetch();
    handleClose();
  };

  const handleChange = (key: string, value: number | string) => {
    let temp: any = modifyInfo;
    if (key !== 'lunch_start_time' && key !== 'lunch_end_time') {
      temp[key] = value;
    } else {
      if (key === 'lunch_start_time') {
        temp.lunch_time = [value, temp.lunch_time[1]];
      } else {
        temp.lunch_time = [temp.lunch_time[0], value];
      }
    }

    setModifyInfo({ ...temp });
  };

  return (
    <>
      <Modal open={carInfoModal} onClose={handleClose}>
        <ModalWrap>
          <div className="head">
            <Typography variant="h5">차량 정보 수정</Typography>
            <GridCloseIcon onClick={handleClose} />
          </div>
          <Divider />
          {modifyInfo && (
            <MainContentWrap>
              <div className="row">
                <TextField
                  label="차량"
                  size="small"
                  disabled
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                  fullWidth
                  value={target.length > 1 ? target.length + '대' : modifyInfo.name}
                />
              </div>
              {target.length === 1 && (
                <>
                  <div className="row">
                    <TextField
                      label="센터"
                      size="small"
                      disabled
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                      fullWidth
                      value={modifyInfo.center_name + ' : ' + modifyInfo.center_address}
                    />
                  </div>

                  <div className="row">
                    <TextField
                      label="차고지"
                      size="small"
                      slotProps={{
                        inputLabel: { shrink: true },
                        input: { readOnly: true },
                      }}
                      fullWidth
                      value={cargoInfo.address}
                      onClick={() => setCoordMap(true)} // coordmap 띄워서 주소 받기
                    />
                  </div>
                </>
              )}
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <CustomTimePicker
                  label="운행 시작 시간"
                  size="small"
                  initValue={modifyInfo.start_time}
                  onChange={(val: string) => handleChange('start_time', val)}
                />{' '}
                ~
                <CustomTimePicker
                  label="운행 종료 시간"
                  size="small"
                  initValue={modifyInfo.end_time}
                  onChange={(val: string) => handleChange('end_time', val)}
                />
              </div>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <CustomTimePicker
                  label="점심시간(시작)"
                  size="small"
                  initValue={modifyInfo.lunch_time[0]}
                  onChange={(val: string) => handleChange('lunch_start_time', val)}
                />{' '}
                ~
                <CustomTimePicker
                  label="점심시간(종료)"
                  size="small"
                  initValue={modifyInfo.lunch_time[1]}
                  onChange={(val: string) => handleChange('lunch_end_time', val)}
                />
              </div>
              <div className="row">
                {regionList && (
                  <FormControl size="small" sx={{ width: '11rem' }}>
                    <InputLabel id="demo-simple-select-label">권역</InputLabel>
                    <Select
                      size="small"
                      label="권역"
                      value={modifyInfo.region}
                      onChange={(e) => {
                        handleChange('region', e.target.value);
                        handleChange(
                          'region_group_seq',
                          _.find(regionList, ['name', e.target.value])?.region_group_seq ?? null,
                        );
                      }}
                    >
                      <MenuItem value={'-'}>-</MenuItem>
                      {regionList.map((item: RegionGroup, index: number) => (
                        <MenuItem key={index} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>
              <div className="row">
                <Typography variant="subtitle2" sx={{ minWidth: '4rem' }}>착지수</Typography>
                <TextField
                  label="최소"
                  size="small"
                  type="number"
                  sx={{ width: '5rem' }}
                  value={modifyInfo.min_dest ?? ''}
                  onChange={(e) => handleChange('min_dest', e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: { inputProps: { style: { textAlign: 'right' } } },
                  }}
                />
                <TextField
                  label="최대"
                  size="small"
                  type="number"
                  sx={{ width: '5rem' }}
                  value={modifyInfo.max_dest ?? ''}
                  onChange={(e) => handleChange('max_dest', e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: { inputProps: { style: { textAlign: 'right' } } },
                  }}
                />
              </div>
              <div className="row">
                <Typography variant="subtitle2" sx={{ minWidth: '4rem' }}>체적(L)</Typography>
                <TextField
                  label="최소"
                  size="small"
                  type="number"
                  sx={{ width: '5rem' }}
                  value={modifyInfo.min_volume ?? ''}
                  onChange={(e) => handleChange('min_volume', e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: { inputProps: { style: { textAlign: 'right' } } },
                  }}
                />
                <TextField
                  label="최대"
                  size="small"
                  type="number"
                  sx={{ width: '5rem' }}
                  value={modifyInfo.max_volume ?? ''}
                  onChange={(e) => handleChange('max_volume', e.target.value)}
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: { inputProps: { style: { textAlign: 'right' } } },
                  }}
                />
              </div>
              <div className="row">
                <Typography variant="subtitle2">출발지</Typography>
                <RadioGroup
                  row
                  value={modifyInfo.start_type ?? 0}
                  onChange={(e) => handleChange('start_type', e.target.value)}
                  //onChange={handleRadioChange}
                >
                  <FormControlLabel value={0} control={<CustomRadio />} label="센터" />
                  <FormControlLabel
                    value={1}
                    control={<CustomRadio disabled={_.find(target, (v) => v.cargo_seq === null) ? true : false} />}
                    label="차고"
                  />
                </RadioGroup>
              </div>
              <div className="row">
                <Typography variant="subtitle2">도착지</Typography>
                <RadioGroup
                  row
                  value={modifyInfo.end_type ?? 0}
                  onChange={(e) => handleChange('end_type', e.target.value)}
                >
                  <FormControlLabel value={1} control={<CustomRadio />} label="마지막배송지" />
                  <FormControlLabel value={0} control={<CustomRadio />} label="센터" />
                  <FormControlLabel
                    value={2}
                    control={<CustomRadio disabled={_.find(target, (v) => v.cargo_seq === null) ? true : false} />}
                    label="차고"
                  />
                </RadioGroup>
              </div>
              <div className="row">
                <Button fullWidth color="success" variant="contained" onClick={handleSaveClick}>
                  저장
                </Button>
                <Button fullWidth color="secondary" variant="contained" onClick={handleClose}>
                  취소
                </Button>
              </div>
            </MainContentWrap>
          )}
        </ModalWrap>
      </Modal>
      <Modal open={coordMap} onClose={() => setCoordMap(false)}>
        <ModalWrap style={{ width: '80%', height: '90%' }}>
          <div className="head">
            <Typography variant="h5">차고지 주소 검색</Typography>
            <GridCloseIcon onClick={() => setCoordMap(false)} />
          </div>
          <CoordMap target={cargoInfo} setInfo={setCargoInfo} mode="CARGO" />
        </ModalWrap>
      </Modal>
    </>
  );
}
